import { db } from "../config/firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { api } from "../config/api";

const isPermissionDenied = (error) => {
  const code = error?.code || "";
  const msg = String(error?.message || "");
  return code === "permission-denied" || msg.toLowerCase().includes("insufficient permissions");
};

// Get All Users
export const getQuotes = async () => {
  const ref = collection(db, "quotes");

  try {
    const res = await getDocs(ref);

    if (res.empty) {
      throw new Error("No quotes found with the specified role");
    }

    const fetchQuotes = res.docs.map((e) => ({
      ...e.data(),
      id: e.id,
    }));

    // Sorting the fetched quotes by quoteNumber or orderNumber in descending order
    const sortedQuotes = fetchQuotes.sort((a, b) => {
      const quoteNumberA = parseInt((a?.quoteNumber || "").substring(4)) || 0;
      const quoteNumberB = parseInt((b?.quoteNumber || "").substring(4)) || 0;
      const orderNumberA = parseInt((a?.orderNumber || "").substring(4)) || 0;
      const orderNumberB = parseInt((b?.orderNumber || "").substring(4)) || 0;

      // If both quotes have orderNumber, compare them first
      if (orderNumberA && orderNumberB) {
        return orderNumberB - orderNumberA;
      }

      // If only one quote has orderNumber, prioritize it
      if (orderNumberA) {
        return -1;
      }
      if (orderNumberB) {
        return 1;
      }

      // If none have orderNumber or both have empty orderNumber, compare by quoteNumber
      return quoteNumberB - quoteNumberA;
    });

    return sortedQuotes;
  } catch (error) {
    // If client Firestore is blocked (rules/App Check), fall back to backend (Admin SDK)
    if (isPermissionDenied(error)) {
      try {
        const proxyRes = await api.get("/firestore/quotes");
        const proxyQuotes = Array.isArray(proxyRes.data) ? proxyRes.data : [];

        const sortedQuotes = proxyQuotes.sort((a, b) => {
          const quoteNumberA = parseInt((a?.quoteNumber || "").substring(4)) || 0;
          const quoteNumberB = parseInt((b?.quoteNumber || "").substring(4)) || 0;
          const orderNumberA = parseInt((a?.orderNumber || "").substring(4)) || 0;
          const orderNumberB = parseInt((b?.orderNumber || "").substring(4)) || 0;

          if (orderNumberA && orderNumberB) return orderNumberB - orderNumberA;
          if (orderNumberA) return -1;
          if (orderNumberB) return 1;
          return quoteNumberB - quoteNumberA;
        });

        return sortedQuotes;
      } catch (proxyError) {
        console.error("Error fetching Quotes via proxy:", proxyError);
        throw proxyError;
      }
    }

    console.error("Error fetching Quotes:", error);
    throw error;
  }
};




export const processAndLoadCountries = async () => {
  try {
    const quotes = await getQuotes();

    // Initialize the counts for load and unload operations
    const countryOperations = {};

    quotes.forEach((quote) => {
      const { loadCountry, unloadCountry } = quote;

      if (loadCountry) {
        if (!countryOperations[loadCountry]) {
          countryOperations[loadCountry] = { load: 0, unload: 0 };
        }
        countryOperations[loadCountry].load += 1;
      }

      if (unloadCountry) {
        if (!countryOperations[unloadCountry]) {
          countryOperations[unloadCountry] = { load: 0, unload: 0 };
        }
        countryOperations[unloadCountry].unload += 1;
      }
    });

    return countryOperations;
  } catch (error) {
    console.error("Error processing and loading countries:", error);
  }
};

// Function to get total load and unload counts
export const getTotalOperationsByCountry = async () => {
  try {
    const countryOperations = await processAndLoadCountries();

    // Convert the operations count into the required array format
    const result = [["Country", "Shipments"]];
    Object.entries(countryOperations).forEach(([country, operations]) => {
      const totalOperations = operations.load + operations.unload;
      result.push([country, totalOperations]);
    });

    return result;
  } catch (error) {
    console.error("Error calculating total operations by country:", error);
  }
};

export const getShipments = async () => {
  const ref = collection(db, 'quotes');

  try {
    // Create a query to fetch quotes that are neither 'pending' nor 'decline'
    const activeQuotesQuery = query(ref, where('status', 'not-in', ['pending', 'decline', 'completed', 'delivered']));
    const res = await getDocs(activeQuotesQuery);

    if (res.empty) {
      throw new Error('No quotes found with the specified criteria');
    }

    const fetchQuotes = res.docs.map((e) => ({
      ...e.data(),
      id: e.id,
    }));

    // Sorting the fetched quotes by quoteNumber or orderNumber in descending order
    const sortedQuotes = fetchQuotes.sort((a, b) => {
      const quoteNumberA = parseInt((a?.quoteNumber || '').substring(4)) || 0;
      const quoteNumberB = parseInt((b?.quoteNumber || '').substring(4)) || 0;
      const orderNumberA = parseInt((a?.orderNumber || '').substring(4)) || 0;
      const orderNumberB = parseInt((b?.orderNumber || '').substring(4)) || 0;

      // If both quotes have orderNumber, compare them first
      if (orderNumberA && orderNumberB) {
        return orderNumberB - orderNumberA;
      }

      // If only one quote has orderNumber, prioritize it
      if (orderNumberA) {
        return -1;
      }
      if (orderNumberB) {
        return 1;
      }

      // If none have orderNumber or both have empty orderNumber, compare by quoteNumber
      return quoteNumberB - quoteNumberA;
    });

    return sortedQuotes;
  } catch (error) {
    console.error('Error fetching Quotes:', error);
    throw error;
  }
};


export const getUserQuoteById = async (userId) => {
  const ref = collection(db, "quotes");
  const q = query(ref, where("userId", "==", userId));

  try {
    const snapshot = await getDocs(q);
    const userQuotes = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return userQuotes;
  } catch (error) {
    console.error("Error fetching user quotes:", error);
    throw error;
  }
};

export const getQuotesById = async (id) => {
  const ref = collection(db, "quotes", id);

  try {
    const res = await getDocs(ref);

    if (res.empty) {
      throw new Error("No quotes found with the specified role");
    }

    const fetchQuotes = res.docs.map((e) => {
      return {
        ...e.data(),
        id: e.id,
      };
    });

    return fetchQuotes;
  } catch (error) {
    console.error("Error fetching Quotes:", error);
    throw error;
  }
};


export const getQuote = async (id) => {
  const ref = doc(db, "quotes", id);

  try {
    const res = await getDoc(ref);

    if (!res.exists()) {
      throw new Error("Quote not found");
    }

    return { ...res.data(), id };
  } catch (error) {
    console.error("Error fetching Quote:", error);
    throw error;
  }
};


//quote record

export const getCompletedShipments = async () => {
  const ref = collection(db, 'quotes');

  try {
    // Create a query to fetch quotes with 'complete' or 'delivered' status
    const completedQuotesQuery = query(ref, where('status', 'in', ['completed', 'delivered']));
    const res = await getDocs(completedQuotesQuery);

    if (res.empty) {
      throw new Error('No quotes found with the specified criteria');
    }

    const fetchQuotes = res.docs.map((e) => ({
      ...e.data(),
      id: e.id,
    }));

    // Sorting the fetched quotes by quoteNumber or orderNumber in descending order
    const sortedQuotes = fetchQuotes.sort((a, b) => {
      const quoteNumberA = parseInt((a?.quoteNumber || '').substring(4)) || 0;
      const quoteNumberB = parseInt((b?.quoteNumber || '').substring(4)) || 0;
      const orderNumberA = parseInt((a?.orderNumber || '').substring(4)) || 0;
      const orderNumberB = parseInt((b?.orderNumber || '').substring(4)) || 0;

      // If both quotes have orderNumber, compare them first
      if (orderNumberA && orderNumberB) {
        return orderNumberB - orderNumberA;
      }

      // If only one quote has orderNumber, prioritize it
      if (orderNumberA) {
        return -1;
      }
      if (orderNumberB) {
        return 1;
      }

      // If none have orderNumber or both have empty orderNumber, compare by quoteNumber
      return quoteNumberB - quoteNumberA;
    });

    return sortedQuotes;
  } catch (error) {
    console.error('Error fetching Quotes:', error);
    throw error;
  }
};

